import { AuditLogMetadata } from './AuditLogs';
import * as MedicalDataTypes from './MedicalDataTypes';
import { PrimaryRecordResourceCategory, Resource, ResourceMetadata, UpdateMetadata } from './MedicalDataTypes';
import * as MedicalProfileFileTypes from './MedicalProfileFiles';
import { RuleSet } from './abac';
import { InsuranceRole, ProviderSpecialty, ProviderType } from './apiTypes/enumTypes';
import { Role } from './LLMTypes';
import { ReportType } from './reports';
import { IndexState, State, Target } from './SearchTypes';
import { FunctionType } from './FunctionType';
import * as OrganizationTypes from './OrganizationTypes';
import * as MedicalProfileCollaborators from './MedicalProfileCollaborators';
import * as UserPreferenceTypes from './UserPreferenceTypes';
import * as UserSettingTypes from './UserSettingTypes';
import { FastenWebhookEventTypes, FastenConnectionStatus, FastenApiMode } from './apiTypes/ehrIntegrationsService';

export type TimeStamps = {
    createdAt: string;
    updatedAt: string;
};

export type UserIdLookup = {
    userId: string;
    userEmailAddress: string;
};

export const userTypes = ['consumer', 'professional'] as const;
export type UserType = (typeof userTypes)[number];

export const userIntents = [
    'manage-self',
    'manage-family',
    'professional-use',
    'collaborator-use',
    'collaborate-with-professional',
] as const;
export type UserIntent = (typeof userIntents)[number];

export type User = {
    userId: string;
    firstName: string;
    lastName: string;
    dob: string;
    preferredName?: string;
    phoneNumber?: string;
    streetAddress1?: string;
    streetAddress2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    avatarUrl?: string;
    registrationPromotionCode?: string;
    type?: UserType;
    userIntents?: UserIntent[];
    hasUpdatedFromRegistration?: boolean;
};

export type UserSubLookupCacheItem = {
    userSub: string;
    userId: string;
};

export type MedicalProfile = {
    medicalProfileId: string;
    /**
     * For medical profiles provisioned by organizations, the ownerUserId will not be defined.
     */
    ownerUserId?: string;
    isFastenEnabled?: boolean;
    /** Reset when a drug allergy is added. */
    noKnownDrugAllergies?: boolean;
};

export type ContactDetailsEntityType = 'medical-profile' | 'care-team-member' | 'organization' | 'organization-group';

export type Sex = 'male' | 'female';
export type Gender = 'male' | 'female' | 'non-binary' | 'declined-answer';
export type CareTeamMemberContactType =
    | 'support-care-team-member'
    | 'provider-care-team-member'
    | 'insurance-care-team-member';
export type ContactType = 'entity-contact' | 'emergency-contact' | CareTeamMemberContactType;

export type ContactDetails = {
    contactDetailsId: string;
    sourceEntityId: string;
    entityType: ContactDetailsEntityType;
    contactType: ContactType;
    firstName?: string | null;
    lastName?: string | null;
    company?: string | null;
    preferredFirstName?: string | null;
    dob?: string | null;
    sex?: Sex | null;
    gender?: Gender | null;
    address1?: string | null;
    address2?: string | null;
    city?: string | null;
    state?: string | null;
    zipCode?: string | null;
    email?: string | null;
    phoneNumber?: string | null;
    relationship?: string | null;
    description?: string | null;
    faxNumber?: string | null;
    url?: string | null;
    role?: string | null;
};

export type SupportCareTeamMemberType =
    | 'emergency'
    | 'transportation'
    | 'case-manager'
    | 'aid-or-assistant'
    | 'family'
    | 'neighbor'
    | 'other';

export type SupportCareTeamMetadata = {
    isActive: boolean;
    supportCareTeamMemberType?: SupportCareTeamMemberType | null;
    otherTypeDescription?: string | null; // Only populated when type is 'other'
};

export type ProviderCareTeamMetadata = {
    providerType: ProviderType;
    providerSpecialty?: ProviderSpecialty | null;
    isActive: boolean;
};

export type InsuranceCareTeamMetadata = {
    insuranceProviderName: string;
    primaryCarrierFirstName?: string;
    primaryCarrierLastName?: string;
    insuranceRole?: InsuranceRole;
    groupNumber: string;
    planNumber: string;
    memberId: string;
    expirationDate?: string;
    isPreferred: boolean;
    isActive: boolean;
};

export type CareTeamMemberTypes = 'support' | 'provider' | 'insurance';

export type CareTeamMemberMetadata = SupportCareTeamMetadata | ProviderCareTeamMetadata | InsuranceCareTeamMetadata;

export type CareTeamMember = {
    careTeamMemberId: string;
    medicalProfileId: string;
    memberType: CareTeamMemberTypes;
    metadata: CareTeamMemberMetadata;
};

export type OrganizationCareTeamMember = Omit<CareTeamMember, 'medicalProfileId'> & {
    organizationId: string;
};

export type MedicalProfileCollaborator = MedicalProfileCollaborators.MedicalProfileCollaborator;

export type CareTeamMemberRelationship = {
    careTeamMemberRelationshipId: string;
    sourceCareTeamMemberId: string;
    targetCareTeamMemberId: string;
};

export type ABACRuleSetEntityType =
    | 'collaborator-invitation'
    | 'medical-profile-collaborator'
    | 'organization-invitation-definition'
    | 'organization-client-invitation';

export type ABACRuleSet = {
    abacRuleSetId: string;
    sourceEntityId: string;
    entityType: ABACRuleSetEntityType;
    ruleSet: {
        ruleSet: RuleSet;
    };
};

export type MedicalProfileCollaboratorInvite = {
    medicalProfileCollaboratorInviteId: string;
    medicalProfileId: string;
    inviteeEmail: string;
    inviteeUserId: string | undefined;
    inviterUserId: string;
    associatedCareTeamMemberId: string | null;
    firstName: string;
    lastName: string;
    message: string | null;
};

export type MedicalProfileOwnershipTransferStatus = 'pending' | 'transferred' | 'denied' | 'revoked';

export type MedicalProfileOwnershipTransfer = {
    medicalProfileOwnershipTransferId: string;
    medicalProfileId: string;
    transferStatus: MedicalProfileOwnershipTransferStatus;
    fromUserId: string;
    toUserId?: string;
    toEmail: string;
    previousOwnerStillWantsAccess: boolean;
    message?: string;
};

export type MedicalProfileInsuranceCard = {
    medicalProfileInsuranceCardId: string;
    medicalProfileId: string;
    description: string;
};

export type MedicalProfileIdCard = {
    medicalProfileIdCardId: string;
    medicalProfileId: string;
    description: string;
};

export type ResourceImage = {
    resourceImageId: string;
    description: string;
};

export type ResourceRecord<
    TResource extends MedicalDataTypes.PrimaryRecordOrOneUpResource = MedicalDataTypes.PrimaryRecordOrOneUpResource,
> = {
    /**
     * Id of the resource record.  This is the projected id value of the resource
     */
    resourceId: string;
    /**
     * External id of the resource.  This is the id of the resource in the source system.  This is used for de-duplication of external system data
     */
    externalId: string | undefined;
    /**
     * Id of the medical profile that owns this resource record.
     */
    medicalProfileId: string;
    /**
     * The date time projected from the resource record.  Used for time-windowed queries
     */
    resourceDateTime: number;
    /**
     * What category of the app does this resource belong to.  Despite the data source and underlying resource type, each
     * resource will map to a primary record resource category.
     */
    resourceCategory: PrimaryRecordResourceCategory<TResource>;
    /**
     * Metadata about the source and creation of the resource
     */
    metadata: ResourceMetadata<TResource>;
    /**
     * Images associated with the resource
     */
    images: ResourceImage[];
    /**
     * The resource itself.  This can be represented as many data formats (primary-record-resource, fhir, etc).
     */
    resource: Resource<TResource>;
    /**
     * Information about the last update to the resource
     */
    updatedMetadata: UpdateMetadata | null;
    /**
     * Updated props of the resource.  This will always be a primary record resource.
     */
    updatedResource: Partial<MedicalDataTypes.MappedPrimaryRecordResourceCategory<TResource>> | null;
    /**
     * Tracks the counts of notes. This is used for the UI to display the number of notes on a resource
     * and to determine if the resource has notes or not.  This is technically de-normalized data, but
     * it is much easier to query this way.  If this gets out of sync, it's not a huge deal.
     */
    noteCount: number;
    /**
     * Tracks the counts of related care team members. This is used for the UI to display the number of care team members
     * associated with the resource.  This is technically de-normalized data, but
     * it is much easier to query this way.  If this gets out of sync, it's not a huge deal.
     */
    careTeamMemberCount: number;
};

export type OneUpHealthUser = {
    /**
     * Primary record ID for oneup health user
     */
    oneUpHealthUserId: string;
    /**
     * User ID for the user in the one up health system.  This is used for audit logging
     */
    oneUpHealthSystemUserId: number;
    medicalProfileId: string;
    accessCode: string | null;
    accessToken: string | null;
    refreshToken: string | null;
    tokenGeneratedOn: string;
};

export type OneUpHealthConnectionName = (fhir2.HumanName | fhir3.HumanName | fhir4.HumanName)[];

export type OneUpHealthSystemConnection = {
    oneUpHealthSystemConnectionId: string;
    oneUpHealthUserId: string;
    type: 'patient-portal';
    connectedHealthSystemId: number;
    authGrantedOn: string | null;
    connectedByUserId?: string;
    /**
     * Has any data been loaded for this connection.  It's possible a user
     * navigates to a portal (which creates a connection), but never logs in.
     * We can assume that happened if absolutely no data
     * is loaded for this connection (hasLoadedDataForConnection === false)
     *
     * can be undefined for backwards compatibility.
     */
    hasLoadedDataForConnection?: boolean;
    /**
     * The name taken from the health connection's patient resource.  Used for a user to help identify the subject of the portal
     */
    oneUpHealthConnectionPatientName?: OneUpHealthConnectionName;
    /**
     * The date of birth taken from the health connection's patient resource.  Used for a user to help identify the subject of the portal
     */
    oneUpHealthConnectionPatientDOB?: string;
    /**
     * The date time of the last refresh of this connection
     */
    lastRefreshDateTime?: string;
    /**
     * The patient record for this connection may not perfectly align with the medical profile information.  Has the user
     * confirmed that this is the correct patient record
     *
     * This may be undefined or if the user has not been prompted to confirm
     */
    isPatientMismatchConfirmed?: boolean;
    /**
     * The ehr patient identifier for the patient record in the connected health system.  This is found on the patient resource identifier with the system:
     * - http:/open.epic.com/FHIR/StructureDefinition/patient-fhir-id (for epic)
     * - https:/fhir-myrecord.cerner.com/r4/{id} (for cerner)
     *
     * This is needed for refreshes via the 1up on-demand refresh API.
     */
    ehrPatientId?: string;
    /**
     * Automatic refresh expiration date.  An either means the ehr does not support on-demand refreshes or this is an old record before refreshes were supported.
     * If this is an old record, the user will need to re-authenticate to get the new refresh token.
     */
    automaticRefreshExpirationDateTime?: string | null;
    /**
     * The user can explicitly disable automatic refreshes.  This is useful if the user does not want to waste portal connect credits for a portal that is not being used.
     */
    userDisabledAutomaticRefresh?: boolean;
};

/** How a Fasten connection was established. Rows written before TEFCA carry no value; read those as 'portal'. */
export type FastenConnectionType = 'portal' | 'tefca';

export type FastenConnection = {
    medicalProfileId: string;
    orgConnectionId: string;
    date: string;
    apiMode: FastenApiMode;
    connectionType?: FastenConnectionType;
    /** Absent for TEFCA connections, which have no catalog portal behind them. */
    endpointId?: string;
    portalId?: string;
    brandId?: string;
    connectionStatus: FastenConnectionStatus;
    platformType?: string;
    requestId?: string;
    externalState?: string;
    /**
     * TEFCA only. Fasten caches directory ids for about two weeks, so the institution name is denormalised onto
     * the row at connect time rather than resolved from this id later.
     */
    tefcaDirectoryId?: string;
    /** FHIR scopes granted by the patient; TEFCA connections always carry `patient/*.read`. */
    scope?: string;
    institutionName?: string;
    institutionWebsite?: string;
    userId?: string;
    /**
     * The date time of the last successful refresh of this connection
     */
    lastRefreshDateTime?: string;
    /**
     * The date time when the refresh capability expires (if applicable based on Fasten's token expiration)
     */
    refreshExpirationDateTime?: string;
    /**
     * The user can explicitly disable automatic refreshes to conserve refresh credits
     */
    userDisabledAutomaticRefresh?: boolean;
    /**
     * Total number of refreshes performed on this connection
     */
    refreshCount?: number;
    /**
     * RFC3339 timestamp when the patient's OAuth consent expires at the health system.
     * Received from Fasten webhook on patient.connection_success.
     */
    consentExpiresAt?: string;
    /**
     * The patient's name extracted from the FHIR Patient resource during data export.
     * Format: "Given Family" (e.g., "Camila Maria Lopez").
     * Only available after the first successful data export.
     */
    patientName?: string;
};

export type FastenEvent = {
    eventId: string;
    type: FastenWebhookEventTypes;
    date: string;
    apiMode: FastenApiMode;
    medicalProfileId: string;
    orgConnectionId: string;
    downloadLink?: string;
    downloadLinks?: {
        url?: string;
        exportType?: string;
        contentType?: string;
    }[];
    taskId?: string;
    orgId?: string;
    stats?: {
        totalResources?: number;
        totalByResourceType?: any;
    };
    failureReason?: string;
};

export type Progress = {
    finished: number;
    total: number;
};

export type RefreshProgressStatus = 'in-progress' | 'complete' | 'failed';

export type MedicalProfileRefreshProgress = {
    medicalProfileRefreshProgressId: string;
    medicalProfileId: string;
    refreshDateTime: string;
    refreshUserId: string;
    refreshType: 'one-up-health';
    retrievalProgress: Progress;
    persistProgress: Progress;
    auditLogRetrievalProgress: Progress;
    status: RefreshProgressStatus;
};

export type FastenRefreshProgress = {
    fastenRefreshProgressId: string;
    medicalProfileId: string;
    orgConnectionId: string;
    refreshDateTime: string;
    refreshUserId: string;
    status: RefreshProgressStatus;
    taskId?: string;
    errorMessage?: string;
};

export type ResourceRelationship = {
    resourceRelationshipId: string;
    sourceResourceId: string;
    targetResourceId: string;
};

export type ResourceCareTeamMember = {
    resourceCareTeamMemberId: string;
    resourceId: string;
    careTeamMemberId: string;
};

export type ResourceNoteEntityType =
    | 'condition-resource'
    | 'medication-resource'
    | 'allergy-resource'
    | 'visit-resource'
    | 'procedure-resource'
    | 'vaccination-resource'
    | 'imaging-resource'
    | 'lab-resource';

export type NoteEntityType =
    | 'medical-profile'
    | 'care-team-member'
    | 'care-team'
    | 'user'
    | 'organization-client-record'
    | 'organization'
    | MedicalDataTypes.PrimaryRecordResourceCategory
    | ResourceNoteEntityType;

export type MedicalProfileOrUserNoteCategoryTagType =
    | 'care-plan'
    | 'financial'
    | 'legal'
    | 'research'
    | 'routines'
    | 'supplies'
    | 'symptom';

export type OrganizationClientRecordNoteCategoryTagType =
    | 'intake'
    | 'visit'
    | 'observation'
    | 'question'
    | 'instructions'
    | 'task'
    | 'reminder'
    | 'issue'
    | 'client-preference'
    | 'change-in-client'
    | 'assessment';

export type NoteCategoryTagType = MedicalProfileOrUserNoteCategoryTagType | OrganizationClientRecordNoteCategoryTagType;

export type NoteFile = {
    noteFileId: string;
    description: string;
    createdDateTime: string;
    contentType?: string; // can be undefined for backwards compatibility
    name?: string; // can be undefined for backwards compatibility
    /** The source note's file this entry was copied from, set on the files of a derived note. */
    sourceFileId?: string;
};

/**
 * What a derived note was produced from. 'note' is another Note (an organization-client-record mirror of a profile
 * note). 'fasten-document-reference' is the Fasten DocumentReference a clinical-document note was generated from;
 * newly generated notes carry it, while notes generated before it existed are identified by their id prefix until
 * they are backfilled.
 */
export const noteSourceEntityTypes = ['note', 'fasten-document-reference'] as const;
export type NoteSourceEntityType = (typeof noteSourceEntityTypes)[number];

/** What a derived note-comment was produced from: 'note-comment' is another NoteComment on the copy's source note. */
export const noteCommentSourceEntityTypes = ['note-comment'] as const;
export type NoteCommentSourceEntityType = (typeof noteCommentSourceEntityTypes)[number];

export type Note = {
    noteId: string;
    entityType: NoteEntityType;
    entityId: string;
    medicalProfileId?: string | undefined;
    organizationId?: string;
    title: string;
    content: string;
    tags: NoteCategoryTagType[];
    images: NoteFile[];
    files: NoteFile[];
    createUserId: string;
    modifiedUserId: string | null;
    isSystemGenerated: boolean;
    /**
     * Optional display label for the creator when it is not an app user — e.g. the physician or the source
     * portal for a system-generated clinical note. The app shows this as "Created By" when present;
     * createUserId is still set for audit/permissions.
     */
    createdBySourceLabel?: string | null;
    /**
     * Unix-ms timestamp of the date the note's CONTENT pertains to — e.g. the clinical visit date for a
     * note imported from a healthcare portal — as opposed to createdAt (when it was imported into Primary
     * Record). When set, the app sorts and displays the note by this instead of createdAt.
     */
    contentDate?: number;
    /**
     * Provenance of a derived note: the entity this note was produced from. For an organization-client-record
     * mirror of a profile note these are 'note', the source note's noteId, and the source note's medicalProfileId.
     * A mirror never carries medicalProfileId itself; the source profile is recorded here instead. Absent on
     * ordinary notes.
     */
    sourceEntityType?: NoteSourceEntityType;
    sourceEntityId?: string;
    sourceMedicalProfileId?: string;
};

export type WithTimestamps<T> = T & TimeStamps;

export type NoteComment = {
    noteCommentId: string;
    noteId: string;
    /**
     * The parent note's medicalProfileId, denormalized onto the comment so all of a profile's note-comments can be
     * fetched in one query (the medicalProfileIdIndex GSI). Optional: comments on user/organization notes have none.
     */
    medicalProfileId?: string;
    comment: string;
    createUserId: string;
    /**
     * Set on a copy of a comment: a comment on an organization-client-record copy of a profile note that mirrors a
     * comment on the source note. `sourceEntityId` is the source comment's id and `sourceCreatedAt` its creation
     * time, which readers show and sort by in place of the copy's own. A copy keeps the source comment's author and
     * never carries `medicalProfileId`, so it stays out of the profile's comment index.
     */
    sourceEntityType?: NoteCommentSourceEntityType;
    sourceEntityId?: string;
    sourceCreatedAt?: string;
    /**
     * The source comment's author as named when the copy was made. While the copy still tracks its note the author's
     * account is resolved live; once it no longer does, the copy is shown under this label and resolves nobody, since
     * the organization may no longer be allowed to see that person.
     */
    createdBySourceLabel?: string;
};

export type AuditLog = {
    auditLogId: string;
    medicalProfileId?: string;
    userId?: string;
    organizationId?: string;
    organizationClientRecordId?: string;
    timestamp: number;
} & AuditLogMetadata;

export type AuditLogComment = {
    auditLogCommentId: string;
    auditLogId: string;
    medicalProfileId?: string;
    comment: string;
    createUserId: string;
};

export const unreadInAppNotificationEntityTypes = [
    'medical-profile-message',
    'medical-profile-activity-comment',
    'medical-profile-note-comment',
] as const;
export type UnreadInAppNotificationEntityType = (typeof unreadInAppNotificationEntityTypes)[number];

export type UnreadInAppNotification = {
    unreadInAppNotificationId: string;
    userId: string;
    entityId: string;
    entityType: UnreadInAppNotificationEntityType;
    medicalProfileId: string;
};

export type ProductCategories = 'setup-and-training' | 'individual-recurring' | 'family-recurring' | 'family-lifetime';

export type SubscriptionProductCategories = 'individual-recurring' | 'family-recurring';

export type OneTimePurchaseProductCategories = 'setup-and-training' | 'family-lifetime';

export type NextPaymentDetails = {
    nextPaymentDate: string;
    nextPaymentAmount: number;
    startDate: string;
};

export type StripeCustomerCachedProducts = {
    cacheTimestamp: string;
    products: ProductCategories[];
    nextPaymentDetails: NextPaymentDetails | null;
};

export type StripeCustomer = {
    stripeCustomerId: string;
    userId: string;
    stripeSystemCustomerId: string;
    /**
     * Stripe api calls to evaluate products can take a while.  This data is leveraged across a lot of service.
     * Caching will keep the api calls to a minimum and improve app performance.
     */
    cachedProducts: StripeCustomerCachedProducts | null;
};

export type PaymentWebhookEvent = {
    paymentWebhookEventId: string;
    userId: string;
    event: string;
};

export type UserProductsAppStore = 'APP_STORE' | 'PLAY_STORE' | 'STRIPE';

export type UserProductEntitlements = 'Individual Plan' | 'Family Plan' | 'Setup and Training';

export type SubscriptionProductTypeIdentifiers =
    | 'family-plan-monthly'
    | 'family-plan-yearly'
    | 'individual-plan-monthly'
    | 'individual-plan-yearly';

export type OneTimePurchaseProductTypeIdentifiers = 'family-plan-lifetime' | 'setup-and-training';

export type ProductTypeIdentifiers = SubscriptionProductTypeIdentifiers | OneTimePurchaseProductTypeIdentifiers;

export type UserProducts = {
    userProductsId: string;
    userId: string;
    sourceAppStore: UserProductsAppStore | null;
    entitlements: UserProductEntitlements[];
    productTypeIdentifiers: ProductTypeIdentifiers[];
    redemptionTimestamp: number | null;
    nextPaymentTimestamp: number | null;
    lastPaymentTimestamp: number | null;
    sourceRegistrationCode?: string;
    /**
     * This was a system/manual entry by the primary record team
     */
    isSystemGenerated: boolean;
};

export type RegistrationCodeFile = {
    fileId: string;
    name: string;
};

export type RegistrationCodeType = 'entitlement' | 'discount';

export type RegistrationCode = {
    registrationCodeId: string;
    code: string;
    type: RegistrationCodeType;
    maxRedemptions: number | null;
    entitlements: UserProductEntitlements[];
    discountRate: number | null;
    durationInMonths: number | null;
    isActive: boolean;
    isDefault?: boolean;
    validityStartDateTime?: string;
    validityEndDateTime?: string;
    expirationDateTime?: string;
    files?: RegistrationCodeFile[];
};

export type UserRegistrationCode = {
    userId: string;
    code: string;
};

export type UserChat = {
    userId: string;
    chatId: string;
    medicalProfileIds: Set<string>;
};

export type UserMedicalProfileChat = {
    userId: string;
    medicalProfileId: string;
    chatId: string;
    createdDateTime: string;
    title?: string;
};

export type ChatReferencedResource = {
    id: string;
    category: PrimaryRecordResourceCategory | 'care-team' | 'file-artifact' | 'note';
    medicalProfileId: string;
};

export type ChatMessageStatus = 'processing' | 'complete' | 'failed';

type ChatMessageBase = {
    chatId: string;
    messageDateTime: number;
    role: Role;
};

export type ChatMessageProcessing = ChatMessageBase & {
    status: 'processing';
};

export type ChatMessageComplete = ChatMessageBase & {
    status?: 'complete'; // Optional for backward compat with existing messages
    text: string;
    referencedResources?: ChatReferencedResource[] | null;
};

export type ChatMessageFailed = ChatMessageBase & {
    status: 'failed';
};

export type ChatMessage = ChatMessageProcessing | ChatMessageComplete | ChatMessageFailed;

export type PDFMonkeyProcessingRecordStatus = 'pending' | 'complete' | 'failed';

export type PDFMonkeyProcessingRecord = {
    pdfMonkeyProcessingRecordId: string;
    medicalProfileId: string;
    generatorUserId: string;
    reportType: ReportType;
    pdfMonkeyDocumentId: string | null;
    status: PDFMonkeyProcessingRecordStatus;
    tempFileId: string | null;
};

export type SharedReport = {
    sharedReportId: string;
    medicalProfileId: string;
    reportType: ReportType;
    shareCode: string;
    creatorUserId: string;
};

export type SearchState = {
    id: string;
    state: State;
    indices: Record<Target, IndexState>;
};

/**
 * Approximate location resolved from a request's source IP via the DB-IP City Lite database
 * (report-service-handler/src/utils/getIpLocation.ts). Only `city`, `region` and `country_name` are read anywhere.
 * Every other field is optional: rows written before the DB-IP switch hold the full ipapi.co response, and new rows
 * only carry the handful of fields DB-IP provides.
 */
export type IPLocation = {
    city: string | null;
    /** State / province. */
    region: string | null;
    country_name: string | null;
    /** ISO-3166 alpha-2. */
    country_code?: string | null;
    continent_code?: string | null;
    in_eu?: boolean | null;
    latitude?: number | null;
    longitude?: number | null;
    ip?: string;
    version?: string;
    region_code?: string;
    country_code_iso3?: string;
    country_capital?: string;
    country_tld?: string;
    postal?: string;
    timezone?: string;
    utc_offset?: string;
    country_calling_code?: string;
    currency?: string;
    currency_name?: string;
    languages?: string;
    country_area?: number;
    country_population?: number;
    asn?: string;
    org?: string;
};

export type ParsedUserAgent = {
    type: string | null;
    vendor: string | null;
    model: string | null;
    osName: string | null;
    browserName: string | null;
    userAgent: string;
};

export type SharedReportView = {
    sharedReportViewId: string;
    sharedReportId: string;
    viewDateTime: string;
    sourceIp: string | null;
    userAgent: string | null;
    ipLocation: IPLocation | null;
    parsedUserAgent: ParsedUserAgent | null;
};

export type EmptyNoteReference = 'empty-note-reference';

export const medicalProfileFileSourceEntityTypes = ['note', 'file', 'directive'] as const;
export type MedicalProfileFileSourceEntityType = (typeof medicalProfileFileSourceEntityTypes)[number];
export type MedicalProfileFileExternalSourceEntityType = Exclude<MedicalProfileFileSourceEntityType, 'file'>;
export const medicalProfileFileEmptySourceEntityId = 'EMPTY';
export type MedicalProfileFileEmptySourceEntityId = typeof medicalProfileFileEmptySourceEntityId;

export type MedicalProfileFile = {
    medicalProfileFileId: string;
    medicalProfileId: string;
    uploadUserId: string;
    contentType: string;
    fileName: string;
    processingStatus: MedicalProfileFileTypes.MedicalProfileFileProcessingStatus;
    failureType?: MedicalProfileFileTypes.MedicalProfileFileFailureType;
    medicalProfileFileFolderId: string | 'root';
    sourceEntityType: MedicalProfileFileSourceEntityType;
    sourceEntityId: string | MedicalProfileFileEmptySourceEntityId;
    sourceFileId?: string;
};

export const systemFolderTypes = ['notes', 'directives'] as const;
export type SystemFolderType = (typeof systemFolderTypes)[number];

export type MedicalProfileFileFolder = {
    medicalProfileId: string;
    medicalProfileFileFolderId: string;
    parentMedicalProfileFileFolderId: string | 'root';
    folderName: string;
    /**
     * Whether or not this folder was automatically generated by the system.
     */
    isGenerated: boolean;
    /**
     * The organization that this folder is associated with.
     */
    organizationId?: string;
    /**
     * The type of system folder, if applicable.
     *
     * System folders are used to store files away from the user's view.
     */
    systemFolderType?: SystemFolderType;
};

export type MedicalProfileFileArtifact = MedicalProfileFileTypes.MedicalProfileFileArtifact;

export type RecurringTaskType =
    | 'one-up-health-refresh'
    | 'registration-code-refresh'
    | 'refresh-notion-feature-flags-database'
    | 'run-scheduled-invoke-lambda-jobs'
    | 'process-daily-organization-usage-metrics'
    | 'process-monthly-organization-usage-metrics'
    | 'clear-all-test-users-data'
    | 'automatic-portal-refresh'
    | 'automatic-fasten-refresh'
    | 'refresh-ip-location-database';

export type RecurringTask = {
    taskType: RecurringTaskType;
    lastRunDateTime: string;
};

export type ScheduledJobType = 'invoke-lambda';

export type ScheduledJob = {
    jobId: string;
    scheduledDateTime: number;
} & {
    jobType: 'invoke-lambda';
    lambdaFunctionName: FunctionType;
    payload: any;
};

export type Organization = OrganizationTypes.Organization;

export type OrganizationUser = OrganizationTypes.OrganizationUser;

export type OrganizationGroup = OrganizationTypes.OrganizationGroup;

export type OrganizationGroupUser = OrganizationTypes.OrganizationGroupUser;

export type OrganizationGroupMedicalProfile = OrganizationTypes.OrganizationGroupMedicalProfile;

export type OrganizationGroupClientRecord = OrganizationTypes.OrganizationGroupClientRecord;

export type OrganizationInvitationDefinition = OrganizationTypes.OrganizationInvitationDefinition;

export type OrganizationClientInvitation = OrganizationTypes.OrganizationClientInvitation;

export type OrganizationUserInvitation = OrganizationTypes.OrganizationUserInvitation;

export type OrganizationClientRecord = OrganizationTypes.OrganizationClientRecord;

export type OrganizationUsageMetric = OrganizationTypes.OrganizationUsageMetric;

export type OrganizationStripeAccount = OrganizationTypes.OrganizationStripeAccount;

export type UserPreference = UserPreferenceTypes.UserPreference;

export type UserSetting = UserSettingTypes.UserSetting;

export const medicalProfileUsageMetricTypes = [
    'chat-message',
    'portal-connect',
    'audio-file-upload',
    'file-upload',
    'fasten-refresh',
] as const;
export type MedicalProfileUsageMetricType = (typeof medicalProfileUsageMetricTypes)[number];

export type MedicalProfileUsageMetric = {
    medicalProfileId: string;
    metricType: MedicalProfileUsageMetricType;
    intervalDate: string;
    value: number;
};

export type MedicalProfileDirectiveFile = {
    fileId: string;
    name: string;
};

export type CodeStatus = 'full-code' | 'partial-code-dni' | 'partial-code-other' | 'dnr' | 'comfort-measures';
export type POARelationship = 'family' | 'friend' | 'professional-advisor' | 'trustee';

export type MedicalProfileDirective = {
    medicalProfileDirectiveId: string;
    medicalProfileId: string;
    codeStatus?: CodeStatus;
    codeStatusNotes?: string;
    codeStatusFiles?: MedicalProfileDirectiveFile[];
    poaHealthcareProxyName?: string;
    poaRelationship?: POARelationship;
    poaPhoneNumber?: string;
    poaFiles?: MedicalProfileDirectiveFile[];
};

export type UserDevicePushToken = {
    userDevicePushTokenId: string;
    userId: string;
    devicePushToken: string;
};

export type SentInviteStatus = 'created' | 'updated' | 'deleted';

export type EmailEntitySentInvite = {
    emailEntitySentInviteId: string;
    sentToEmail: string;
    sourceEntityId: string;
    status: SentInviteStatus;
    sequenceNumber: number;
    medicalProfileId: string;
};

export type MedicalProfileConfiguredResourceInviteEmail = {
    medicalProfileConfiguredResourceInviteEmailId: string;
    medicalProfileId: string;
    createdByUserId: string;
    inviteeEmail: string;
};

// Mirrors the reCAPTCHA handler's own outcomes, plus not_assessed for the sign-up paths that never
// reach an assessment at all (federated providers and admin-created users). Storing that explicitly
// is what lets a card distinguish "does not apply" from "the check failed".
export type SignupAssessmentOutcome =
    | 'disabled'
    | 'valid'
    | 'low_score'
    | 'missing_token'
    | 'unrecognized_site_key'
    | 'invalid_token'
    | 'action_mismatch'
    | 'assessment_error'
    | 'not_assessed';

// Written by the PreSignUp trigger, read once when the new-user Chat card is built. Keyed by a
// SHA-256 of the email because no userId exists yet at sign-up time. Deliberately holds no raw
// email address and no reCAPTCHA token.
export type SignupAssessment = {
    emailHash: string;
    ttl: number;
    assessedAt: string;
    triggerSource: string;
    outcome: SignupAssessmentOutcome;
    mode?: string;
    decision?: string;
    score?: number;
    reasons?: string[];
    siteKeySuffix?: string;
    invalidReason?: string;
    action?: string;
    hostname?: string;
    androidPackageName?: string;
    iosBundleId?: string;
    errorCategory?: string;
    httpStatus?: number;
    signupPlatform?: string;
    signupAppVersion?: string;
    // The Cognito identity provider a federated sign-up came through, as configured on the user pool
    // (`Google`, `SignInWithApple`, `CorporateSSO`). Absent on password sign-ups.
    signupProvider?: string;
};
